/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Modal,
  Fade,
  FormControl,
  RadioGroup
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useSWR from 'swr';
import Input from '../form/Input';
import RegularInput from '../input/RegularInput';
import { weekDays } from '../../helpers/weekDays';
import SortableTable from '../list/sortableTable';
import DatePickerTextField from '../../views/leads/steps/components/DatePickerTextField';
import TimeComponent from '../input/TimeComponent';
import api from '../../api';
import {
  calculateEndTime,
  getCurrency,
  getMediaRate
} from '../../utils/general';
import AddChecklistModal from '../modals/AddChecklistModal';
import { ModalContainer } from '../modals/GlobalConfirmModal';
import CustomRadio from '../form/CustomRadio';
import { BorderLinearProgress } from '../../views/clients/ImportClients';
import useAuthStore from '../../store/zustand/auth';
import useCompanyStore from '../../store/zustand/company';
import RecomputeRatesButton from '../calc-v3/RecomputeRatesButton';

interface Technician {
  hours: number[];
  id: number;
  name: string; // Array of hours worked per day (7 days)
}
const daysOfWeek = weekDays.map((day: any) => day.name);

interface Props {
  destinationId: string;
  mutate: () => void;
  saveStepper: ({ next }: any) => void;
  sendValues: React.Dispatch<React.SetStateAction<any>>;
  setCurrentServiceTime: React.Dispatch<React.SetStateAction<number>>;
  setValues: React.Dispatch<React.SetStateAction<any>>;
  values: any;
}

interface ServiceTime {
  days: string[];
  hours: number;
  setupTime: string;
  teardownTime: number;
  technicians: string[];
  travelTime: number;
}

const WorkLoading: React.FC<Props> = function WorkLoading({
  destinationId,
  mutate,
  setCurrentServiceTime,
  saveStepper,
  sendValues,
  setValues,
  values
}) {
  const [technicians, setTechnicians] = useState<Technician[]>([
    { hours: Array(7).fill(0), id: 1, name: 'Technician 1' }
  ]);
  const { t } = useTranslation('translation', {
    keyPrefix: 'LEAD.CREATE.WORK_LOADING'
  });
  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'GENERAL'
  });
  const userState = useAuthStore((state) => state);
  const companyState = useCompanyStore((state) => state);
  const calculatorV3Enabled: boolean = Boolean(
    (companyState.company as unknown as { calculatorV3Enabled?: boolean })
      ?.calculatorV3Enabled,
  );
  const [workloadingInFlight, setWorkloadingInFlight] = useState<boolean>(
    false,
  );
  const [workloadingError, setWorkloadingError] = useState<string | null>(null);
  const [workloadingAppliedAt, setWorkloadingAppliedAt] = useState<
    string | null
  >(null);
  const [workloadingGaps, setWorkloadingGaps] = useState<string[]>([]);
  const [totalCleanableSqft, setTotalCleanableSqft] = useState<string>('');
  // Calc v3 density/soil multipliers — operator-applied at the whole-job
  // level for the 80/20 fast path. Per-area breakdown still wins when
  // values.serviceAddresses[].areas have density/soilLevel populated.
  const [siteDensity, setSiteDensity] = useState<string>('standard');
  const [siteSoilLevel, setSiteSoilLevel] = useState<string>('standard');

  // Monthly scheduling pattern — "the [Nth] [weekday] of the month."
  // Matches the legacy ServiceTime.tsx picker so operators see the same
  // options in either place. Cost math still treats Monthly as 12
  // visits/year (12/52 ≈ 0.231 visits/week); these selections only drive
  // WHICH day a visit lands on for scheduling.
  const [monthlyWeek, setMonthlyWeek] = useState<string>('First');
  const [monthlyDay, setMonthlyDay] = useState<string>('Monday');

  // Hours-driven mode — for contracts where square footage is irrelevant
  // (Day Porter, on-site patrol, dedicated shifts). Either the selected
  // service type's computeMode is HOURS_DRIVEN, OR the operator flips the
  // override switch. When active, sqft inputs hide and the calc becomes
  // hoursPerDay × daysPerWeek × operatingWeeks.
  const [hoursPerDay, setHoursPerDay] = useState<string>('8');
  const [hoursCrewSize, setHoursCrewSize] = useState<string>('1');
  const [hoursOverride, setHoursOverride] = useState<boolean>(false);
  // Operating weeks per year. Defaults to 52 (year-round). Schools run
  // ~36 weeks (9 months × 4), summer-season contracts are ~13 weeks,
  // etc. Operator sets this per bid.
  const [operatingWeeks, setOperatingWeeks] = useState<string>('52');
  const [defaultServiceCode, setDefaultServiceCode] = useState<string>(
    'NIGHTLY_OFFICE_CLEAN'
  );
  const [workloadingPreview, setWorkloadingPreview] = useState<any>(null);
  const [visitCompletionWindow, setVisitCompletionWindow] = useState<string>('4');
  // Catalog rows reshaped to the legacy chip shape so the inline rate-edit
  // UI keeps working. New ServiceCatalogApi is the source; legacy
  // WorkloadingApi.listTenantRates is only used as a fallback when the
  // catalog endpoint isn't available (older BE).
  const [tenantRates, setTenantRates] = useState<
    Array<{
      computeMode?: 'SQFT_DRIVEN' | 'HOURS_DRIVEN';
      effectiveRateSqftPerHour: number;
      globalDefaultRateSqftPerHour: number;
      hasTenantOverride: boolean;
      notes?: string;
      serviceCode: string;
      serviceName: string;
    }>
  >([]);
  const [rateEditDraft, setRateEditDraft] = useState<string>('');
  const [rateEditOpen, setRateEditOpen] = useState<boolean>(false);
  const [rateSaveInFlight, setRateSaveInFlight] = useState<boolean>(false);
  // Visit days — Mon–Fri checked by default, Sun/Sat off.
  const [visitDays, setVisitDays] = useState<boolean[]>([
    false, // Sun
    true, // Mon
    true, // Tue
    true, // Wed
    true, // Thu
    true, // Fri
    false // Sat
  ]);
  const [hourlyRate, setHourlyRate] = useState<number>(0);
  const [taskChecklist, setTaskChecklist] = useState<any[]>([]);
  const [imagesRequired, setImagesRequired] = useState<boolean>(false);
  const [startTime, setTimeStart] = React.useState<Dayjs | null>(
    dayjs().hour(8).minute(0)
  );
  const [resourcesApi, setResourcesApi] = useState<boolean>(false);
  const [setupTime, setSetupTime] = useState<string>('0'); // Initial value in hours
  const [teardownTime, setTeardownTime] = useState<number>(0); // Initial value in hours
  const [travelTime, setTravelTime] = useState<number>(0); // Initial value in hours
  const [resources, setResources] = useState<any[]>([]);
  const [medianRate, setMedianRate] = useState<number>(0);
  const [openAddChecklistModal, setOpenAddChecklistModal] =
    useState<boolean>(false);
  const [frequency, setFrequency] = useState<string>('Weekly');
  const [generateShiftModal, setGenerateShiftModal] = useState<boolean>(false);
  useState<number>();
  const [serviceTimeApi, setServiceTimeApi] = useState<boolean>(false);
  const [serviceTimes, setServiceTimes] = useState<any>();
  const currentSerrviceTimeCounter = useRef<number>(0);
  const [progressBar, setProgressBar] = useState<boolean>(false);
  const [serviceTimeName, setServiceTimeName] = useState<string>(
    `${t('serviceName')}`
  );
  const isPremiumPlan = userState.plan !== 'Calculator';

  const { data: resourcesData } = useSWR(
    resourcesApi ? { skillIds: [] } : null,
    api.UserResocesApi.getSkillResource
  );

  const { data: serviceTimeData } = useSWR(
    serviceTimeApi
      ? [values?.id, serviceTimes?.[currentSerrviceTimeCounter.current]]
      : null,
    api.LeadServiceTimesApi.createLeadServicesTime
  );

  // Handle change in hours worked
  const handleHoursChange = (
    technicianId: number,
    dayIndex: number,
    value: number
  ) => {
    setTechnicians((prev) =>
      prev.map((tech) =>
        tech.id === technicianId
          ? {
              ...tech,
              hours: tech.hours.map((hour, index) =>
                index === dayIndex ? value : hour
              )
            }
          : tech
      )
    );
  };

  // Add a new technician
  const addTechnician = () => {
    const newId =
      technicians.length > 0 ? technicians[technicians.length - 1].id + 1 : 1;
    const newTechnician: Technician = {
      hours: Array(7).fill(0),
      id: newId,
      name: `Technician ${newId}`
    };
    setTechnicians((prev) => [...prev, newTechnician]);
  };

  // Remove a technician
  const removeTechnician = (technicianId: number) => {
    setTechnicians((prev) => prev.filter((tech) => tech.id !== technicianId));
  };

  // Calculate the total hours worked
  const totalHours = technicians.reduce(
    (total, tech) => total + tech.hours.reduce((sum, hours) => sum + hours, 0),
    0
  );

  // ── Calculator v3: Preview → Apply workflow ──────────────────────────────
  // Two-step flow so the operator sees what the math produced BEFORE the grid
  // is mutated. Preview calls /workloading/preview and stores the result.
  // Apply wipes the tech rows and re-populates from the stored preview.
  // For Weekly/Biweekly: operator picks N weekdays from the checkbox row.
  // For Monthly: there's exactly ONE recurring day (e.g. "2nd Monday"), so
  // the effective day list is just [that day's index].
  const WEEKDAY_TO_INDEX: Record<string, number> = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6
  };
  const selectedDayIndices =
    frequency === 'Monthly'
      ? [WEEKDAY_TO_INDEX[monthlyDay] ?? 1]
      : visitDays.map((on, i) => (on ? i : -1)).filter((i) => i >= 0);

  const selectedRate = tenantRates.find(
    (r) => r.serviceCode === defaultServiceCode
  );
  // Effective hours-driven mode: service type declares HOURS_DRIVEN OR
  // the operator flipped the override switch. Either path shows the
  // simpler "hours per day × days" layout and skips the sqft calc.
  const hoursDrivenMode =
    hoursOverride || selectedRate?.computeMode === 'HOURS_DRIVEN';

  // When operator picks a different service, reset the override flag so
  // the newly-selected service's computeMode is the source of truth.
  // Without this, picking Day Porter (HOURS_DRIVEN) → then picking Nightly
  // (SQFT_DRIVEN) could leave the UI stuck in hours mode if anything ever
  // caused hoursOverride to latch true. Operator can re-enable the
  // override after picking if they want to force hours mode on an
  // SQFT_DRIVEN service.
  useEffect(() => {
    setHoursOverride(false);
    setWorkloadingPreview(null);
  }, [defaultServiceCode]);

  // Load the tenant's effective rates once on mount so we know whether the
  // selected service is on a tenant override or the global default. Reads
  // from the new service catalog (CONTRACT-only) and reshapes to the
  // legacy chip shape so downstream UI doesn't have to change. Falls back
  // to the legacy listTenantRates if the catalog endpoint 404s on an
  // older API build.
  useEffect(() => {
    if (!calculatorV3Enabled) return;
    (async () => {
      try {
        const catalog = await api.ServiceCatalogApi.list('CONTRACT');
        setTenantRates(
          catalog.map((c) => ({
            computeMode: c.computeMode,
            effectiveRateSqftPerHour: c.baseRateSqftPerHour,
            globalDefaultRateSqftPerHour:
              c.referenceDefaultRate ?? c.baseRateSqftPerHour,
            hasTenantOverride: c.hasTenantRow,
            notes: c.notes,
            serviceCode: c.code,
            serviceName: c.name
          }))
        );
      } catch {
        try {
          const legacy = await api.WorkloadingApi.listTenantRates();
          setTenantRates(legacy);
        } catch {
          // non-fatal — UI falls back to the hardcoded list below
        }
      }
    })();
  }, [calculatorV3Enabled]);

  const saveTenantRate = async () => {
    const parsed = Number(rateEditDraft);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    setRateSaveInFlight(true);
    try {
      const rates = await api.WorkloadingApi.setTenantRate(
        defaultServiceCode,
        parsed
      );
      setTenantRates(rates);
      setRateEditOpen(false);
      setWorkloadingPreview(null); // invalidate — new rate changes the math
    } catch {
      // keep the editor open on failure; user sees stale state and can retry
    } finally {
      setRateSaveInFlight(false);
    }
  };

  const resetTenantRateToDefault = async () => {
    setRateSaveInFlight(true);
    try {
      const rates = await api.WorkloadingApi.setTenantRate(
        defaultServiceCode,
        null
      );
      setTenantRates(rates);
      setRateEditOpen(false);
      setWorkloadingPreview(null);
    } finally {
      setRateSaveInFlight(false);
    }
  };

  const runWorkloadingPreview = async () => {
    setWorkloadingError(null);
    setWorkloadingInFlight(true);
    try {
      const frequencyDaysCount = Math.max(1, selectedDayIndices.length);
      // Visits/week. The BE treats each service-day as a separate visit
      // (a nightly M-F clean = 5 visits/week). Scale by frequency:
      //   Weekly:   full visit count per week
      //   Biweekly: every other week → half of the visit count
      //   Monthly:  one visit per month regardless of day picker →
      //             12 visits/year = 12/52 ≈ 0.231 visits/week
      let visitsPerWeek: number;
      if (frequency === 'Monthly') {
        visitsPerWeek = 12 / 52;
      } else if (frequency === 'Biweekly') {
        visitsPerWeek = frequencyDaysCount * 0.5;
      } else {
        visitsPerWeek = frequencyDaysCount;
      }
      const completionWindowHours = Math.max(
        0.25,
        Number(visitCompletionWindow) || 4
      );

      const breakdownAreas =
        values?.serviceAddresses?.flatMap((addr: any) =>
          (addr?.areas ?? []).map((area: any) => ({
            cleanableSqft: Number(area?.cleanableSqft ?? 0),
            density: area?.density ?? siteDensity,
            soilLevel: area?.soilLevel ?? siteSoilLevel
          }))
        ) ?? [];
      // 80/20 path: when no per-area breakdown exists but the operator
      // entered Total Cleanable SF, synthesize a single area carrying the
      // job-level density/soil so the BE multipliers actually apply.
      const synthesizedArea =
        breakdownAreas.length === 0 && Number(totalCleanableSqft) > 0
          ? [
              {
                cleanableSqft: Number(totalCleanableSqft),
                density: siteDensity,
                soilLevel: siteSoilLevel
              }
            ]
          : [];
      const areas = breakdownAreas.length > 0 ? breakdownAreas : synthesizedArea;

      const existingTasks =
        values?.serviceTimes?.flatMap((st: any) =>
          (st?.services ?? []).flatMap((service: any) =>
            (service?.tasks ?? []).map((task: any) => ({
              code: task?.parentTask?.id ?? task?.name ?? 'CUSTOM',
              measure: task?.measure,
              measurePerHour: Number(task?.measurePerHour ?? 0),
              name: task?.name,
              quantity: Number(task?.quantity ?? 0)
            }))
          )
        ) ?? [];

      // In HOURS_DRIVEN mode, the task's quantity is PER-VISIT hours and
      // measurePerHour = 1, so the engine computes hoursPerVisit = quantity
      // exactly. BE then does hoursPerYear = hoursPerVisit × visitsPerWeek
      // × operatingWeeks — with visitsPerWeek above set to the actual visit
      // count per week, that gives the expected annual total.
      const hoursPerVisitInput =
        Number(hoursPerDay) > 0
          ? Number(hoursPerDay)
          : completionWindowHours;
      const tasks = hoursDrivenMode
        ? [
            {
              code: `HOURS_${defaultServiceCode}`,
              measure: 'Unit',
              measurePerHour: 1,
              name: `${selectedRate?.serviceName ?? 'On-site hours'} (time-based)`,
              quantity: hoursPerVisitInput
            }
          ]
        : existingTasks;

      const sqftFromInput = Number(totalCleanableSqft);
      // Hours-driven: crewSize drives completionWindowHours via
      //   hoursPerVisit / crewSize → ceil(hoursPerVisit / window) = crewSize
      // so the BE arrives at the operator's intended crew size.
      const hoursDrivenCrewSize = Math.max(1, Number(hoursCrewSize) || 1);
      const hoursDrivenCompletionWindow =
        hoursPerVisitInput / hoursDrivenCrewSize;
      // Supervisor cost source-of-truth: per-lead, not the company-wide
      // SVR/SVH static-cost rows (those were a duplicate of what the lead
      // already captures via AssignSupervisor). When supervision is disabled
      // or no supervisor is assigned, send 0/0 so the workloading line item
      // is $0 — don't fall back to company defaults.
      const SUPERVISOR_PERIODS_PER_YEAR: Record<string, number> = {
        Annually: 1,
        Biannually: 2,
        Biweekly: 26,
        FullTime: 52, // full-time = 52 wks of supervisorTime hrs/wk
        Monthly: 12,
        Quarterly: 4,
        Weekly: 52
      };
      const supervisorEnabled = Boolean(
        values?.supervisorEnabled && values?.supervisor
      );
      const supervisorHoursPerYear = supervisorEnabled
        ? Number(values?.supervisorTime ?? 0) *
          (SUPERVISOR_PERIODS_PER_YEAR[values?.supervisorFrequency] ?? 0)
        : 0;
      const supervisorRate = supervisorEnabled
        ? Number(values?.supervisor?.rate ?? 0)
        : 0;

      const request = {
        areas: hoursDrivenMode ? [] : areas,
        completionWindowHours: hoursDrivenMode
          ? hoursDrivenCompletionWindow
          : completionWindowHours,
        defaultServiceCode,
        frequency: {
          frequencyDaysCount,
          operatingWeeks: Math.max(1, Math.min(52, Number(operatingWeeks) || 52)),
          visitsPerWeek
        },
        medianLaborRate: medianRate > 0 ? medianRate : hourlyRate || 20,
        service: {
          setupHours: Number(setupTime) || 0,
          tasks,
          teardownHours: Number(teardownTime) || 0,
          travelHours: Number(travelTime) || 0
        },
        supervisorHoursPerYear,
        supervisorRate,
        targetMargin: 0.33,
        totalCleanableSqftOverride:
          !hoursDrivenMode &&
          Number.isFinite(sqftFromInput) &&
          sqftFromInput > 0
            ? sqftFromInput
            : undefined
      };

      const output = await api.WorkloadingApi.preview(request);
      setWorkloadingPreview({
        ...output,
        hoursPerWeek: output.hoursPerYear / 52,
        visitsPerWeek
      });
      setWorkloadingGaps(output.configGaps ?? []);
      setWorkloadingAppliedAt(null);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        'Workloading preview failed. Check Cost Calculator inputs and try again.';
      setWorkloadingError(message);
      setWorkloadingPreview(null);
    } finally {
      setWorkloadingInFlight(false);
    }
  };

  const applyWorkloadingToGrid = () => {
    if (!workloadingPreview) return;
    const crewSize = Math.max(1, workloadingPreview.crewSize || 1);
    const hoursPerWeek = workloadingPreview.hoursPerWeek || 0;
    const activeDays = selectedDayIndices.length > 0 ? selectedDayIndices : [1, 2, 3, 4, 5];
    const hoursPerTechPerActiveDay =
      hoursPerWeek / (crewSize * activeDays.length);

    // Rebuild the tech list from scratch: exactly crewSize rows, each with
    // hours on the selected days, zero on the rest. Wipes any prior manual
    // edits — that's the intended behavior after the operator has reviewed
    // the preview and confirmed.
    const next: Technician[] = [];
    for (let i = 0; i < crewSize; i += 1) {
      next.push({
        hours: Array(7)
          .fill(0)
          .map((_, dayIdx) =>
            activeDays.includes(dayIdx)
              ? Number(hoursPerTechPerActiveDay.toFixed(2))
              : 0
          ),
        id: i + 1,
        name: `Technician ${i + 1}`
      });
    }
    setTechnicians(next);
    setWorkloadingAppliedAt(workloadingPreview.calculatedAt);
  };

  const toggleVisitDay = (index: number) => {
    setVisitDays((prev) =>
      prev.map((on, i) => (i === index ? !on : on))
    );
    setWorkloadingPreview(null); // invalidate stale preview when inputs change
  };

  const generateServiceTimes = (): ServiceTime[] => {
    setProgressBar(true);
    const serviceTimeMap: Record<string, ServiceTime> = {};

    // Iterate over each technician
    technicians.forEach((technician) => {
      technician.hours.forEach((hours, dayIndex) => {
        if (hours > 0) {
          // Unique key based on hours worked and technicians
          const key = `${hours}-${technician.hours.join(',')}`;
          if (!serviceTimeMap[key]) {
            serviceTimeMap[key] = {
              days: [], // List of days
              hours,
              setupTime,
              teardownTime,
              technicians: [],
              travelTime // List of technicians
            };
          }

          // Add the day to the Service Time
          if (!serviceTimeMap[key].days.includes(daysOfWeek[dayIndex])) {
            serviceTimeMap[key].days.push(daysOfWeek[dayIndex]);
          }

          // Add the technician to the Service Time if not already included
          if (!serviceTimeMap[key].technicians.includes(technician.name)) {
            serviceTimeMap[key].technicians.push(technician.name);
          }
        }
      });
    });
    const startTimeFormatted = startTime?.format('HH:mm') || '8:00';
    const serviceTimes = Object.values(serviceTimeMap).map((item: any) => ({
      destinationId: destinationId.toString(),
      endDate: values?.endDate,
      endTime: calculateEndTime(startTimeFormatted, item.hours),
      frequency,
      // Monthly pattern lives in frequencyWeek + frequencyDays[0] (one day).
      // Weekly/Biweekly use the full frequencyDays list from the grid.
      ...(frequency === 'Monthly'
        ? { frequencyDays: [monthlyDay], frequencyWeek: monthlyWeek }
        : { frequencyDays: item.days }),
      services: [
        {
          description: `${t('serviceDescription')}`,
          name: `${serviceTimeName}`,
          setupTime: setupTime || undefined,
          tasks: [
            {
              description: `${t('taskDescription')}`,
              isImageRequired: imagesRequired,
              measure: 'Unit',
              measurePerHour: '1',
              mediaRate: Number(medianRate),
              name: `${t('taskName')}`,
              quantity: item.hours * item.technicians.length,
              resourcesAssigned: item.technicians.length,
              taskChecklist: taskChecklist?.map((item: any) => ({
                completed: item.completed,
                name: item['to-do']
              })),
              technicians: item.technicians.map((tech: any, index: number) => ({
                hrAvailable: 40,
                id: index,
                name: tech,
                profilePic: null,
                rate: medianRate,
                status: 'AVAILABLE'
              }))
            }
          ],
          teardownTime: teardownTime || undefined
        }
      ],
      startDate: values?.startDate,
      startTime: startTimeFormatted,
      travelTime: travelTime || undefined
    }));
    setServiceTimes(serviceTimes);
    setServiceTimeApi(true);
    return Object.values(serviceTimeMap); // Convert the map into an array of Service Times
  };

  useEffect(() => {
    if (resourcesData !== undefined) {
      setResources(resourcesData);
      const rate = getMediaRate(resourcesData);
      setMedianRate(rate);
      setHourlyRate(rate);
      setResourcesApi(false);
    }
  }, [resourcesData]);

  useEffect(() => {
    if (serviceTimeData) {
      setServiceTimeApi(false);
      currentSerrviceTimeCounter.current += 1;
      setTimeout(() => {
        if (currentSerrviceTimeCounter.current < serviceTimes?.length) {
          setServiceTimeApi(true);
        } else {
          setProgressBar(false);
          setGenerateShiftModal(false);
          sendValues({ ...values });
          setCurrentServiceTime(-1);
          setTimeout(() => {
            mutate();
          }, 500);
        }
      }, 500);
    }
  }, [serviceTimeData]);

  useEffect(() => {
    setResourcesApi(true);
  }, []);

  const progress =
    serviceTimes && serviceTimes.length > 0
      ? Math.round(
          (currentSerrviceTimeCounter.current * 100) / serviceTimes.length
        )
      : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography color="primary.300" variant="heading_500">
          {t('title')}
        </Typography>
        <Typography color="secondary.400" variant="body_200">
          {t('subtitle')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box alignItems="center" display="grid" gridTemplateColumns="200px 1fr">
          <Typography color="primary.300" variant="heading_500">
            {t('serviceNameTitle')}:
          </Typography>
          <RegularInput
            defaultValue={serviceTimeName}
            onChange={(e: any) => setServiceTimeName(e)}
            type="text"
            value={serviceTimeName}
          />
        </Box>
        <Box alignItems="center" display="grid" gridTemplateColumns="200px 1fr">
          <Typography color="primary.300" variant="heading_500">
            {t('frequency')}:
          </Typography>
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="service-frequency"
              name="service-frequency"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const frequency = event.target.value;
                setFrequency(frequency);
              }}
            >
              <Box display="flex" gap="48px">
                <FormControlLabel
                  checked={frequency === 'Weekly'}
                  control={<CustomRadio />}
                  // disabled={values?.readOnly}
                  label={
                    <Typography color="mainText.200" variant="body_300">
                      {t('weekly')}
                    </Typography>
                  }
                  value="Weekly"
                />
                <FormControlLabel
                  checked={frequency === 'Biweekly'}
                  control={<CustomRadio />}
                  // disabled={values?.readOnly}
                  label={
                    <Typography color="mainText.200" variant="body_300">
                      {t('biweekly')}
                    </Typography>
                  }
                  value="Biweekly"
                />
                <FormControlLabel
                  checked={frequency === 'Monthly'}
                  control={<CustomRadio />}
                  label={
                    <Typography color="mainText.200" variant="body_300">
                      Monthly
                    </Typography>
                  }
                  value="Monthly"
                />
              </Box>
            </RadioGroup>
          </FormControl>
        </Box>
        {calculatorV3Enabled && (
          <Box
            sx={{
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.100',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 2
            }}
          >
            <Box
              sx={{
                alignItems: 'flex-start',
                display: 'flex',
                gap: 2,
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography color="primary.400" variant="body_300">
                  Workloading
                </Typography>
                <Typography color="secondary.400" variant="body_100">
                  {hoursDrivenMode
                    ? 'This service is time-based — enter hours per visit and the days you\u2019ll visit. Square footage is irrelevant.'
                    : 'Enter total cleanable SF, pick a service type and the days you\u2019ll visit. Click Preview to see hours, crew size, and price before filling the grid.'}
                </Typography>
              </Box>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexShrink: 0,
                  gap: 1
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hoursOverride}
                      onChange={() => {
                        setHoursOverride((v) => !v);
                        setWorkloadingPreview(null);
                      }}
                      size="small"
                    />
                  }
                  label={
                    <Typography color="mainText.200" variant="body_100">
                      Time-based override
                    </Typography>
                  }
                />
                <RecomputeRatesButton
                  leadId={values?.id}
                  leadStatus={values?.status}
                />
              </Box>
            </Box>

            {/* Inputs row 1: SF + Service + Window + density/soil. Align
                items at the TOP so the "Using default / Edit rate" subline
                under Service Type doesn't push the other inputs out of
                horizontal alignment. */}
            <Box
              sx={{
                alignItems: 'flex-start',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              {hoursDrivenMode ? (
                <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
                  <Typography color="mainText.200" variant="body_200">
                    Hours per visit
                  </Typography>
                  <TextField
                    fullWidth
                    inputProps={{ min: 0.25, step: 0.5 }}
                    onChange={(e) => {
                      setHoursPerDay(e.target.value);
                      setWorkloadingPreview(null);
                    }}
                    size="small"
                    type="number"
                    value={hoursPerDay}
                  />
                </Box>
              ) : (
                <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
                  <Typography color="mainText.200" variant="body_200">
                    Total Cleanable SF
                  </Typography>
                  <TextField
                    fullWidth
                    inputProps={{ min: 0 }}
                    onChange={(e) => {
                      setTotalCleanableSqft(e.target.value);
                      setWorkloadingPreview(null);
                    }}
                    size="small"
                    type="number"
                    value={totalCleanableSqft}
                  />
                </Box>
              )}
              <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
                <Typography color="mainText.200" variant="body_200">
                  Service Type
                </Typography>
                <Select
                  fullWidth
                  onChange={(e) => {
                    setDefaultServiceCode(String(e.target.value));
                    setRateEditOpen(false);
                    setWorkloadingPreview(null);
                  }}
                  size="small"
                  value={defaultServiceCode}
                >
                  {(tenantRates.length > 0
                    ? tenantRates.map((r) => ({
                        code: r.serviceCode,
                        label:
                          r.computeMode === 'HOURS_DRIVEN'
                            ? `${r.serviceName} — time-based${r.hasTenantOverride ? ' (your rate)' : ''}`
                            : `${r.serviceName} (${r.effectiveRateSqftPerHour} sqft/hr${r.hasTenantOverride ? ' — your rate' : ''})`
                      }))
                    : [
                        // Hardcoded fallback when neither catalog nor
                        // legacy rate endpoint is available. Contract
                        // services only — Strip & Wax / Post Construction
                        // are PROJECT work and live in a different flow.
                        {
                          code: 'NIGHTLY_OFFICE_CLEAN',
                          label: 'Nightly Office Clean (2500 sqft/hr)'
                        },
                        {
                          code: 'GENERAL_OFFICE_CLEAN',
                          label: 'General Office Clean (2200 sqft/hr)'
                        },
                        {
                          code: 'MEDICAL_OFFICE_CLEAN',
                          label: 'Medical Office Clean (1500 sqft/hr)'
                        },
                        {
                          code: 'DAY_PORTER',
                          label: 'Day Porter Service (1800 sqft/hr)'
                        }
                      ]
                  ).map((opt) => (
                    <MenuItem key={opt.code} value={opt.code}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {selectedRate && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: 1,
                      mt: 0.5
                    }}
                  >
                    <Typography
                      color={
                        selectedRate.hasTenantOverride
                          ? 'success.main'
                          : 'secondary.400'
                      }
                      variant="body_100"
                    >
                      {selectedRate.hasTenantOverride
                        ? `Your rate: ${selectedRate.effectiveRateSqftPerHour} sqft/hr (default ${selectedRate.globalDefaultRateSqftPerHour})`
                        : `Using default: ${selectedRate.effectiveRateSqftPerHour} sqft/hr`}
                    </Typography>
                    <Button
                      onClick={() => {
                        setRateEditDraft(
                          String(selectedRate.effectiveRateSqftPerHour)
                        );
                        setRateEditOpen(!rateEditOpen);
                      }}
                      size="small"
                      sx={{ minWidth: 0, py: 0 }}
                      variant="text"
                    >
                      {rateEditOpen ? 'Close' : 'Edit rate'}
                    </Button>
                  </Box>
                )}
                {rateEditOpen && selectedRate && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mt: 1
                    }}
                  >
                    <RegularInput
                      minVal={0}
                      onChange={(e: any) => setRateEditDraft(String(e))}
                      type="number"
                      value={rateEditDraft}
                    />
                    <Button
                      color="primary"
                      disabled={rateSaveInFlight}
                      onClick={saveTenantRate}
                      size="small"
                      variant="contained"
                    >
                      Save for tenant
                    </Button>
                    {selectedRate.hasTenantOverride && (
                      <Button
                        color="secondary"
                        disabled={rateSaveInFlight}
                        onClick={resetTenantRateToDefault}
                        size="small"
                        variant="outlined"
                      >
                        Reset to default
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
              {hoursDrivenMode ? (
                <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
                  <Typography color="mainText.200" variant="body_200">
                    Crew size
                  </Typography>
                  <TextField
                    fullWidth
                    inputProps={{ min: 1, step: 1 }}
                    onChange={(e) => {
                      setHoursCrewSize(e.target.value);
                      setWorkloadingPreview(null);
                    }}
                    size="small"
                    type="number"
                    value={hoursCrewSize}
                  />
                </Box>
              ) : (
                <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
                  <Typography color="mainText.200" variant="body_200">
                    Hours Available per Visit
                  </Typography>
                  <TextField
                    fullWidth
                    inputProps={{ min: 0.25, step: 0.25 }}
                    onChange={(e) => {
                      setVisitCompletionWindow(e.target.value);
                      setWorkloadingPreview(null);
                    }}
                    size="small"
                    type="number"
                    value={visitCompletionWindow}
                  />
                </Box>
              )}
              <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
                <Typography color="mainText.200" variant="body_200">
                  Operating weeks/year
                </Typography>
                <TextField
                  fullWidth
                  helperText={
                    Number(operatingWeeks) < 52
                      ? `${(52 - Number(operatingWeeks)).toFixed(0)} weeks off`
                      : undefined
                  }
                  inputProps={{ max: 52, min: 1, step: 1 }}
                  onChange={(e) => {
                    setOperatingWeeks(e.target.value);
                    setWorkloadingPreview(null);
                  }}
                  size="small"
                  type="number"
                  value={operatingWeeks}
                />
              </Box>
              {!hoursDrivenMode && (
                <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
                  <Typography color="mainText.200" variant="body_200">
                    Site density
                  </Typography>
                  <Select
                    fullWidth
                    onChange={(e) => {
                      setSiteDensity(String(e.target.value));
                      setWorkloadingPreview(null);
                    }}
                    size="small"
                    value={siteDensity}
                  >
                    <MenuItem value="sparse">Sparse (open layout)</MenuItem>
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="dense">Dense (many obstacles)</MenuItem>
                    <MenuItem value="industrial">Industrial / heavy equip</MenuItem>
                  </Select>
                </Box>
              )}
              {!hoursDrivenMode && (
                <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
                  <Typography color="mainText.200" variant="body_200">
                    Soil level
                  </Typography>
                  <Select
                    fullWidth
                    onChange={(e) => {
                      setSiteSoilLevel(String(e.target.value));
                      setWorkloadingPreview(null);
                    }}
                    size="small"
                    value={siteSoilLevel}
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="heavy">Heavy</MenuItem>
                  </Select>
                </Box>
              )}
            </Box>

            {/* Inputs row 2: Visit days (Weekly / Biweekly) OR
                Monthly pattern picker. Same outer height either way so
                the Preview button below doesn't jump. */}
            {frequency === 'Monthly' ? (
              <Box>
                <Typography color="mainText.200" variant="body_200">
                  Repeats on
                </Typography>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 1
                  }}
                >
                  <Select
                    onChange={(e) => {
                      setMonthlyWeek(String(e.target.value));
                      setWorkloadingPreview(null);
                    }}
                    size="small"
                    sx={{ minWidth: 140 }}
                    value={monthlyWeek}
                  >
                    <MenuItem value="First">First</MenuItem>
                    <MenuItem value="Second">Second</MenuItem>
                    <MenuItem value="Third">Third</MenuItem>
                    <MenuItem value="Fourth">Fourth</MenuItem>
                    <MenuItem value="Last">Last</MenuItem>
                  </Select>
                  <Select
                    onChange={(e) => {
                      setMonthlyDay(String(e.target.value));
                      setWorkloadingPreview(null);
                    }}
                    size="small"
                    sx={{ minWidth: 160 }}
                    value={monthlyDay}
                  >
                    <MenuItem value="Sunday">Sunday</MenuItem>
                    <MenuItem value="Monday">Monday</MenuItem>
                    <MenuItem value="Tuesday">Tuesday</MenuItem>
                    <MenuItem value="Wednesday">Wednesday</MenuItem>
                    <MenuItem value="Thursday">Thursday</MenuItem>
                    <MenuItem value="Friday">Friday</MenuItem>
                    <MenuItem value="Saturday">Saturday</MenuItem>
                  </Select>
                  <Typography color="secondary.400" variant="body_200">
                    of the month — 12 visits/year
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography color="mainText.200" variant="body_200">
                  Visit Days ({selectedDayIndices.length} selected)
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 1
                  }}
                >
                  {daysOfWeek.map((day: string, idx: number) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visitDays[idx]}
                          onChange={() => toggleVisitDay(idx)}
                          size="small"
                        />
                      }
                      key={day}
                      label={
                        <Typography variant="body_200">{day}</Typography>
                      }
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Preview button — different quantity signal per mode:
                hours mode needs hours/day > 0, sqft mode needs total
                cleanable SF > 0. Either way, at least one visit day. */}
            <Box>
              <Button
                color="primary"
                disabled={
                  workloadingInFlight ||
                  selectedDayIndices.length === 0 ||
                  (hoursDrivenMode
                    ? !(Number(hoursPerDay) > 0)
                    : !(Number(totalCleanableSqft) > 0))
                }
                onClick={runWorkloadingPreview}
                variant="outlined"
              >
                {workloadingInFlight ? 'Computing…' : 'Preview'}
              </Button>
            </Box>

            {/* Results panel */}
            {workloadingPreview && !workloadingAppliedAt && (
              <Box
                sx={{
                  bgcolor: 'common.white',
                  border: '1px solid',
                  borderColor: 'success.light',
                  borderRadius: 1,
                  p: 2
                }}
              >
                <Typography color="primary.400" variant="body_300">
                  Preview — review before applying
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    mt: 1
                  }}
                >
                  <Typography variant="body_200">
                    Hours per visit:{' '}
                    <strong>
                      {workloadingPreview.hoursPerVisit.toFixed(2)}
                    </strong>
                  </Typography>
                  <Typography variant="body_200">
                    Crew size:{' '}
                    <strong>{workloadingPreview.crewSize}</strong> tech(s)
                  </Typography>
                  <Typography variant="body_200">
                    Hours per week:{' '}
                    <strong>
                      {workloadingPreview.hoursPerWeek.toFixed(2)}
                    </strong>
                  </Typography>
                  <Typography variant="body_200">
                    Hours per year:{' '}
                    <strong>
                      {workloadingPreview.hoursPerYear.toFixed(0)}
                    </strong>
                  </Typography>
                </Box>
                <Typography
                  color="secondary.400"
                  sx={{ mt: 1 }}
                  variant="body_100"
                >
                  Pricing + margin are on the Analysis tab.
                </Typography>
                {workloadingGaps.length > 0 && (
                  <Typography
                    color="warning.main"
                    sx={{ mt: 1 }}
                    variant="body_100"
                  >
                    Notes: {workloadingGaps.join(', ')}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    color="primary"
                    onClick={applyWorkloadingToGrid}
                    variant="contained"
                  >
                    Apply to grid (replaces existing tech rows)
                  </Button>
                  <Button
                    color="secondary"
                    onClick={() => setWorkloadingPreview(null)}
                    variant="text"
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}

            {workloadingAppliedAt && (
              <Typography color="success.main" variant="body_100">
                Applied {new Date(workloadingAppliedAt).toLocaleString()} —
                grid populated with {workloadingPreview?.crewSize ?? '?'}{' '}
                tech(s) across {selectedDayIndices.length} day(s).
              </Typography>
            )}

            {workloadingError && (
              <Typography color="error.main" variant="body_100">
                {workloadingError}
              </Typography>
            )}
          </Box>
        )}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '200px' }}>
                  <Typography variant="body_200">{t('technician')}</Typography>
                </TableCell>{' '}
                {/* Ancho aumentado */}
                {daysOfWeek.map((day) => (
                  <TableCell align="center" key={day}>
                    <Typography variant="body_200">{day}</Typography>
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Typography variant="body_200">{t('weeklyTotal')}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {technicians.map((technician) => {
                const weeklyTotal = technician.hours.reduce(
                  (sum, hours) => sum + hours,
                  0
                ); // Total semanal
                return (
                  <TableRow key={technician.id}>
                    <TableCell>{technician.name}</TableCell>
                    {technician.hours.map((hour, index) => (
                      <TableCell
                        align="center"
                        key={`${technician.id}-${daysOfWeek[index]}`}
                      >
                        <RegularInput
                          align="right"
                          minVal={0}
                          onChange={(e: any) =>
                            handleHoursChange(technician.id, index, Number(e))
                          }
                          type="number"
                          value={hour.toString()}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      {Number(weeklyTotal.toFixed(2))}
                    </TableCell>
                    {/* Botón para eliminar técnico */}
                    <TableCell align="center">
                      <Button
                        color="secondary"
                        onClick={() => removeTechnician(technician.id)}
                        variant="text"
                      >
                        <i className="custom-icon icon-delete" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Botón para agregar técnicos */}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            justifyContent: 'space-between'
          }}
        >
          <Button color="primary" onClick={addTechnician} variant="outlined">
            <i className="custom-icon icon-add" />
            &nbsp;
            <Typography variant="primary">{t('addTechnician')}</Typography>
          </Button>
          <Typography variant="primary">{`${t(
            'totalHours'
          )}: ${Number(totalHours.toFixed(2))}`}</Typography>
        </Box>
      </Box>
      {isPremiumPlan && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            justifyContent: 'flex-start'
          }}
        >
          <Typography color="primary.300" variant="heading_500">
            {t('checklistTitle')}
          </Typography>
          <Typography color="secondary.400" variant="body_200">
            {t('checklistDescription')}
          </Typography>
          <SortableTable
            addManagement
            bodyKeys={['to-do']}
            onChange={(newChecklist) => {
              const checklist = newChecklist.map((item: any) => {
                return { ...item, completed: false };
              });
              setTaskChecklist(checklist);
              // setValue({
              //     ...value,
              //     taskChecklist: checklist
              // });
            }}
            type="generic-input"
            values={taskChecklist}
          />
          <Button
            color="primary"
            onClick={() => setOpenAddChecklistModal(true)}
            sx={{ justifyContent: 'flex-start', width: 300 }}
            variant="text"
          >
            <i className="custom-icon icon-add" />
            {t('addChecklist')}
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            width: '100%'
          }}
        >
          <Box
            sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography color="primary.300" variant="heading_500">
                {t('setupTimeTitle')}
              </Typography>
              <Typography color="secondary.400" variant="body_200">
                {t('setupTimeDescription')}
              </Typography>
              <TimeComponent
                reverseOrder
                setTime={(newTime: number) => setSetupTime(newTime.toString())}
                time={parseFloat(setupTime)}
              />
            </Box>
          </Box>
          <Box
            sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography color="primary.300" variant="heading_500">
                {t('teardownTimeTitle')}
              </Typography>
              <Typography color="secondary.400" variant="body_200">
                {t('teardownTimeDescription')}
              </Typography>
              <TimeComponent
                reverseOrder
                setTime={(newTime: number) => setTeardownTime(newTime)}
                time={teardownTime}
              />
            </Box>
          </Box>
          <Box
            sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography color="primary.300" variant="heading_500">
                {t('travelTimeTitle')}
              </Typography>
              <Typography color="secondary.400" variant="body_200">
                {t('travelTimeDescription')}
              </Typography>
              <TimeComponent
                reverseOrder
                setTime={(newTime: number) => setTravelTime(newTime)}
                time={travelTime}
              />
            </Box>
          </Box>
        </Box>
        {isPremiumPlan && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              width: '100%'
            }}
          >
            <Box
              sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography color="primary.300" variant="heading_500">
                  {t('startTimeTitle')}
                </Typography>
                <Typography color="secondary.400" variant="body_200">
                  {t('startTimeDescription')}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopTimePicker
                    onChange={(newValue) => {
                      setTimeStart(newValue);
                    }}
                    renderInput={(params) => (
                      <DatePickerTextField {...params} />
                    )}
                    value={startTime}
                  />
                </LocalizationProvider>
              </Box>
            </Box>

            <Box
              sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography color="primary.300" variant="heading_500">
                  {t('imageRequiredTitle')}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={imagesRequired}
                      color="primary"
                      onChange={(e) => setImagesRequired(e.target.checked)}
                    />
                  }
                  label={
                    <Typography color="secondary.400" variant="body_200">
                      {t('imageRequiredDescription')}
                    </Typography>
                  }
                />
              </Box>
            </Box>
            <Box sx={{ flex: 1 }} />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            width: '100%'
          }}
        >
          <Box
            sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1 }}
          >
            <Typography
              color="primary.300"
              sx={{ marginBottom: 2 }}
              variant="title"
            >
              {t('costAddons')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography color="primary.300" variant="heading_500">
                {t('hourlyRateTitle')}
              </Typography>
              <Typography color="secondary.400" variant="body_200">
                {hourlyRate > 0
                  ? t('hourlyRateDescription', [getCurrency(hourlyRate)])
                  : 'No technician rates available yet — enter a rate manually (or add active technicians with rates in the People module).'}
              </Typography>
              <RegularInput
                align="right"
                defaultValue={medianRate.toString()}
                minVal={0}
                onChange={(e) => setMedianRate(Number(e))}
                type="number"
                value={medianRate.toString()}
              />
            </Box>
          </Box>
          <Box
            sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography color="primary.300" variant="heading_500">
                {t('equipmentCostTitle')} ($)
              </Typography>
              <Typography color="secondary.400" variant="body_200">
                {t('equipmentCostDescription')}
              </Typography>
              <RegularInput
                align="right"
                defaultValue={values?.equipmentCost?.toString()}
                minVal={0}
                onChange={(e) =>
                  setValues({ ...values, equipmentCost: Number(e) })
                }
                type="number"
                value={values?.equipmentCost?.toString()}
              />
            </Box>
          </Box>
          <Box
            sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography color="primary.300" variant="heading_500">
                {t('suppliesCostTitle')} ($)
              </Typography>
              <Typography color="secondary.400" variant="body_200">
                {t('suppliesCostDescription')}
              </Typography>
              <RegularInput
                align="right"
                defaultValue={values?.suppliesCost?.toString()}
                minVal={0}
                onChange={(e) =>
                  setValues({ ...values, suppliesCost: Number(e) })
                }
                type="number"
                value={values?.suppliesCost?.toString()}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          justifyContent: 'flex-end'
        }}
      >
        <Button
          onClick={() => {
            setCurrentServiceTime(-1);
          }}
          sx={{
            width: 300
          }}
          variant="cancel"
        >
          {translate('cancelButton')}
        </Button>
        <Button
          onClick={() => {
            setGenerateShiftModal(true);
          }}
          sx={{
            width: 300
          }}
          variant="contained"
        >
          {t('generateShift')}
        </Button>
      </Box>
      {(openAddChecklistModal || generateShiftModal) && (
        <Modal
          closeAfterTransition
          open
          className="scrollbar"
          onClose={() => {}}
        >
          <Fade in>
            <ModalContainer className="scrollbar">
              {openAddChecklistModal && (
                <AddChecklistModal
                  setValue={(value) => {
                    const checklistNew = value?.list?.map((item: any) => {
                      return {
                        completed: false,
                        'to-do': item
                      };
                    });
                    setTaskChecklist(
                      taskChecklist?.length > 0
                        ? [...taskChecklist, ...checklistNew]
                        : [...checklistNew]
                    );
                    setOpenAddChecklistModal(false);
                  }}
                />
              )}
              {generateShiftModal && (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                    padding: 4
                  }}
                >
                  <Typography color="primary.300" variant="heading_500">
                    {t('generateShiftInformationTitle')}
                  </Typography>
                  <Typography color="secondary.400" variant="body_200">
                    {t('generateShiftInformationDescription')}
                  </Typography>
                  {progressBar && (
                    <Box>
                      <BorderLinearProgress
                        color="primary"
                        style={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          width: '680px'
                        }}
                        value={progress}
                        variant="determinate"
                      />
                      <Typography
                        color="mainText.main"
                        sx={{ fontSize: 22 }}
                        variant="primary-bold"
                      >
                        {`${progress}%`}
                      </Typography>
                    </Box>
                  )}
                  <Button
                    disabled={progressBar}
                    onClick={() => {
                      generateServiceTimes();
                    }}
                    sx={{
                      width: 300
                    }}
                    variant="contained"
                  >
                    {translate('continueButton')}
                  </Button>
                </Box>
              )}
            </ModalContainer>
          </Fade>
        </Modal>
      )}
    </Box>
  );
};

WorkLoading.defaultProps = {};

export default WorkLoading;
